import * as i0 from '@angular/core';
import { EventEmitter, Directive, Output, HostListener, Input, Pipe, Injectable, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule, DecimalPipe } from '@angular/common';
import isMap from 'lodash/isMap';
import { Subject } from 'rxjs';
import get from 'lodash/get';

class DzGridConstants {
}
DzGridConstants.COLUMNS = 24;
DzGridConstants.BREAKPOINTS = {
    xs: 0,
    sm: 600,
    md: 768,
    lg: 1024,
    xl: 1440
};

const TAB = 'Tab';
const SPACE = ' ';
const ENTER = 'Enter';
const COMMA = ',';
const SEMICOLON = ';';
const ARROW_UP = 'ArrowUp';
const ARROW_RIGHT = 'ArrowRight';
const ARROW_DOWN = 'ArrowDown';
const ARROW_LEFT = 'ArrowLeft';
const HOME = 'Home';
const END = 'End';

/**
 * Helper to make the tippy the same width as the trigger element
 */
const sameWidth = {
    enabled: true,
    fn: ({ instance, state }) => {
        const triggerReferenceWidth = `${state.rects.reference.width}px`;
        if (state.styles.popper.width !== triggerReferenceWidth) {
            state.styles.popper.width = triggerReferenceWidth;
            // force update the popper instance, so it can reposition the dropdown properly
            instance.update();
        }
    },
    name: 'sameWidth',
    phase: 'beforeWrite',
    requires: ['computeStyles']
};

class ResponsiveDisplayConstants {
}
/**
 * @deprecated Please use DzGridConstants.BREAKPOINTS, Daisy grid classes, or Sass breakpoint functions instead
 */
ResponsiveDisplayConstants.DEVICE_WIDTH = {
    MOBILE_MAX: 767,
    TABLET_MIN: 768,
    TABLET_MAX: 1024,
    DESKTOP_MIN: 1366
};

/**
 * Directive to add 'click outside' functionality to any element
 *
 * The directive detects clicks anywhere on the page that isn't on the element the directive
 * is attached to and emits an event.
 *
 * An example below is for a button that opens an Actions Drawer, but when you click elsewhere on the page,
 * the drawer will be hidden.
 * ```html
 * <div (dz-click-outside)="hideDrawer()">
 * 	<button (click)="showDrawer()">Show Drawer</button>
 * 	<div *ngIf="showActions">
 * 		<!-- Action Drawer Content -->
 * 	</div>
 * </div>
 * ```
 */
class ClickOutsideDirective {
    constructor() {
        this.clickOutsideEvent = new EventEmitter();
        this.clickInside = false;
    }
    onElementClick() {
        this.clickInside = true;
    }
    onDocumentClick() {
        if (!this.clickInside) {
            this.clickOutsideEvent.emit();
        }
        this.clickInside = false;
    }
}
ClickOutsideDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ClickOutsideDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
ClickOutsideDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: ClickOutsideDirective, selector: "[dz-click-outside]", outputs: { clickOutsideEvent: "dz-click-outside" }, host: { listeners: { "click": "onElementClick()", "document:click": "onDocumentClick()" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ClickOutsideDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[dz-click-outside]'
                }]
        }], propDecorators: { clickOutsideEvent: [{
                type: Output,
                args: ['dz-click-outside']
            }], onElementClick: [{
                type: HostListener,
                args: ['click']
            }], onDocumentClick: [{
                type: HostListener,
                args: ['document:click']
            }] } });

class CurrencyFormatDirective {
    constructor(el, renderer, decimalPipe) {
        this.el = el;
        this.renderer = renderer;
        this.decimalPipe = decimalPipe;
        this.locale = 'en-US';
        this.currencyChars = new RegExp('[.,]', 'g');
    }
    ngAfterViewChecked() {
        this.format(this.el.nativeElement.value); // will format all values
    }
    format(val) {
        // check for non-number characters and replace/remove them
        const numberToFormat = parseInt(String(val).replace(this.currencyChars, ''));
        // format the number (add commas)
        const numberAfterFormat = this.decimalPipe.transform(numberToFormat, '1.0', this.locale);
        // replace the input value with formatted numbers
        this.renderer.setProperty(this.el.nativeElement, 'value', numberAfterFormat);
    }
}
CurrencyFormatDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CurrencyFormatDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1.DecimalPipe }], target: i0.ɵɵFactoryTarget.Directive });
CurrencyFormatDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: CurrencyFormatDirective, selector: "[dzCurrencyFormat]", inputs: { locale: ["dzCurrencyFormatLocale", "locale"] }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CurrencyFormatDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dzCurrencyFormat]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1.DecimalPipe }]; }, propDecorators: { locale: [{
                type: Input,
                args: ['dzCurrencyFormatLocale']
            }] } });

/**
 * Determines the position to which to scroll a panel in order for an option to be into view.
 * @param itemOffset Offset of the item from the top of the panel.
 * @param itemHeight Height of the item.
 * @param currentScrollPosition Current scroll position of the panel.
 * @param panelHeight Height of the panel.
 * @docs-private
 */
function getItemScrollPosition(itemOffset, itemHeight, currentScrollPosition, panelHeight) {
    if (itemOffset < currentScrollPosition) {
        return itemOffset;
    }
    if (itemOffset + itemHeight > currentScrollPosition + panelHeight) {
        return Math.max(0, itemOffset - panelHeight + itemHeight);
    }
    return currentScrollPosition;
}

/**
 * This class manages keyboard events for selectable lists. If you pass it an array
 * of items, it will set the active item correctly when arrow events occur.
 */
class ListKeyManager {
    constructor(items) {
        this._activeItemIndex = -1;
        this._activeItem = null;
        this._homeAndEnd = true;
        this._vertical = true;
        this._horizontal = false;
        this._wrap = false;
        this._itemArr = [];
        /**
         * Stream that emits any time the TAB key is pressed, so components can react
         * when focus is shifted off of the list.
         */
        this.tabOut = new Subject();
        /** Stream that emits whenever the active item of the list manager changes. */
        this.change = new Subject();
        /**
         * Predicate function that can be used to check whether an item should be skipped
         * by the key manager. By default, disabled items are skipped.
         */
        this._skipPredicateFn = (item) => item.disabled;
        this.updateItems(items);
    }
    /**
     * Allows the items to be updated programatically, eg if the available items are filtered
     * @param items The array or map of arrays of the items to be iterated through
     */
    updateItems(items) {
        if (isMap(items)) {
            this._itemArr = [];
            items.forEach((val) => (this._itemArr = [...this._itemArr, ...val]));
        }
        else {
            this._itemArr = items;
        }
    }
    /**
     * Sets the predicate function that determines which items should be skipped by the
     * list key manager.
     * @param predicate Function that determines whether the given item should be skipped.
     */
    skipPredicate(predicate) {
        this._skipPredicateFn = predicate;
        return this;
    }
    /**
     * Configures wrapping mode, which determines whether the active item will wrap to
     * the other end of list when there are no more items in the given direction.
     * @param shouldWrap Whether the list should wrap when reaching the end.
     */
    withWrap(shouldWrap = true) {
        this._wrap = shouldWrap;
        return this;
    }
    /**
     * Configures whether the key manager should be able to move the selection vertically.
     * @param enabled Whether vertical selection should be enabled.
     */
    withVerticalOrientation(enabled = true) {
        this._vertical = enabled;
        return this;
    }
    /**
     * Configures the key manager to move the selection horizontally.
     * @param enabled Whether horizontal selection should be enabled.
     */
    withHorizontalOrientation(enabled = true) {
        this._horizontal = enabled;
        return this;
    }
    /**
     * Configures the key manager to activate the first and last items
     * respectively when the Home or End key is pressed.
     * @param enabled Whether pressing the Home or End key activates the first/last item.
     */
    withHomeAndEnd(enabled = true) {
        this._homeAndEnd = enabled;
        return this;
    }
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    setActiveItem(item) {
        const previousActiveItem = this._activeItem;
        this.updateActiveItem(item);
        if (this._activeItem !== previousActiveItem) {
            this.change.next(this._activeItemIndex);
        }
    }
    /**
     * Sets the active item depending on the key event passed in.
     * @param event Keyboard event to be used for determining which element should be active.
     */
    onKeydown(event) {
        const key = event.key;
        switch (key) {
            case TAB:
                this.tabOut.next();
                return;
            case ARROW_DOWN:
                if (this._vertical) {
                    this.setNextItemActive();
                    break;
                }
                else {
                    return;
                }
            case ARROW_UP:
                if (this._vertical) {
                    this.setPreviousItemActive();
                    break;
                }
                else {
                    return;
                }
            case ARROW_RIGHT:
                if (this._horizontal) {
                    this.setNextItemActive();
                    break;
                }
                else {
                    return;
                }
            case ARROW_LEFT:
                if (this._horizontal) {
                    this.setPreviousItemActive();
                    break;
                }
                else {
                    return;
                }
            case HOME:
                if (this._homeAndEnd) {
                    this.setFirstItemActive();
                    break;
                }
                else {
                    return;
                }
            case END:
                if (this._homeAndEnd) {
                    this.setLastItemActive();
                    break;
                }
                else {
                    return;
                }
            default:
                return;
        }
        event.preventDefault();
    }
    /** Index of the currently active item. */
    get activeItemIndex() {
        return this._activeItemIndex;
    }
    /** The active item. */
    get activeItem() {
        return this._activeItem;
    }
    /** Sets the active item to the first enabled item in the list. */
    setFirstItemActive() {
        this._setActiveItemByIndex(0, 1);
    }
    /** Sets the active item to the last enabled item in the list. */
    setLastItemActive() {
        this._setActiveItemByIndex(this._itemArr.length - 1, -1);
    }
    /** Sets the active item to the next enabled item in the list. */
    setNextItemActive() {
        this._activeItemIndex < 0 ? this.setFirstItemActive() : this._setActiveItemByDelta(1);
    }
    /** Sets the active item to a previous enabled item in the list. */
    setPreviousItemActive() {
        this._activeItemIndex < 0 && this._wrap ? this.setLastItemActive() : this._setActiveItemByDelta(-1);
    }
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    updateActiveItem(item) {
        const itemArray = this._itemArr;
        const index = typeof item === 'number' ? item : itemArray.indexOf(item);
        if (itemArray?.length) {
            const activeItem = itemArray[index];
            // Explicitly check for `null` and `undefined` because other falsy values are valid.
            this._activeItem = activeItem === null ? null : activeItem;
            this._activeItemIndex = index;
        }
    }
    /**
     * This method sets the active item, given a list of items and the delta between the
     * currently active item and the new active item. It will calculate differently
     * depending on whether wrap mode is turned on.
     */
    _setActiveItemByDelta(delta) {
        this._wrap ? this._setActiveInWrapMode(delta) : this._setActiveInDefaultMode(delta);
    }
    /**
     * Sets the active item properly given "wrap" mode. In other words, it will continue to move
     * down the list until it finds an item that is not disabled, and it will wrap if it
     * encounters either end of the list.
     */
    _setActiveInWrapMode(delta) {
        const items = this._itemArr;
        for (let i = 1; i <= items.length; i++) {
            const index = (this._activeItemIndex + delta * i + items.length) % items.length;
            const item = items[index];
            if (!this._skipPredicateFn(item)) {
                this.setActiveItem(index);
                return;
            }
        }
    }
    /**
     * Sets the active item properly given the default mode. In other words, it will
     * continue to move down the list until it finds an item that is not disabled. If
     * it encounters either end of the list, it will stop and not wrap.
     */
    _setActiveInDefaultMode(delta) {
        this._setActiveItemByIndex(this._activeItemIndex + delta, delta);
    }
    /**
     * Sets the active item to the first enabled item starting at the index specified. If the
     * item is disabled, it will move in the fallbackDelta direction until it either
     * finds an enabled item or encounters the end of the list.
     */
    _setActiveItemByIndex(index, fallbackDelta) {
        const items = this._itemArr;
        if (!items[index]) {
            return;
        }
        while (this._skipPredicateFn(items[index])) {
            index += fallbackDelta;
            if (!items[index]) {
                return;
            }
        }
        this.setActiveItem(index);
    }
    destroy() {
        this.change.complete();
        this.tabOut.complete();
    }
}

class IsArrayPipe {
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    transform(value) {
        return Array.isArray(value);
    }
}
IsArrayPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: IsArrayPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
IsArrayPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: IsArrayPipe, name: "isArray" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: IsArrayPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'isArray' }]
        }] });

class SlugifyPipe {
    transform(input, format = '-') {
        if (!input) {
            return input;
        }
        return input
            .toString()
            .toLowerCase()
            .replace(/\s+/g, format) // Replace spaces with -
            .replace(/[^a-zA-Z0-9]+/g, format) // Remove all non-word chars
            .replace(new RegExp(`${format}+`, 'g'), format) // Replace multiple - with single -
            .replace(new RegExp(`^${format}+`), '') // Trim - from start of text
            .replace(new RegExp(`${format}+$`), ''); // Trim - from end of text
    }
}
SlugifyPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SlugifyPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
SlugifyPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SlugifyPipe, name: "slugify" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SlugifyPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'slugify' }]
        }] });

class TypeofPipe {
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    transform(value) {
        return typeof value;
    }
}
TypeofPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TypeofPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
TypeofPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TypeofPipe, name: "typeof" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TypeofPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'typeof' }]
        }] });

class DzDomService {
    constructor(componentFactoryResolver, appRef, injector) {
        this.componentFactoryResolver = componentFactoryResolver;
        this.appRef = appRef;
        this.injector = injector;
    }
    appendComponentToBody(component) {
        document.body.appendChild(this.getDomElement(component));
    }
    prependComponentToBody(component) {
        document.body.insertBefore(this.getDomElement(component), document.body.childNodes[0]);
    }
    getDomElement(component) {
        // 1. Create a component reference from the component
        const componentRef = this.componentFactoryResolver.resolveComponentFactory(component).create(this.injector);
        // 2. Attach component to the appRef so that it's inside the ng component tree
        this.appRef.attachView(componentRef.hostView);
        // 3. Get DOM element from component
        return get(componentRef, 'hostView.rootNodes[0]');
    }
}
DzDomService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: DzDomService, deps: [{ token: i0.ComponentFactoryResolver }, { token: i0.ApplicationRef }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
DzDomService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: DzDomService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: DzDomService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i0.ComponentFactoryResolver }, { type: i0.ApplicationRef }, { type: i0.Injector }]; } });

class UtilModule {
}
UtilModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: UtilModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
UtilModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: UtilModule, declarations: [ClickOutsideDirective, CurrencyFormatDirective, SlugifyPipe, TypeofPipe, IsArrayPipe], imports: [CommonModule], exports: [ClickOutsideDirective, CurrencyFormatDirective, SlugifyPipe, TypeofPipe, IsArrayPipe] });
UtilModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: UtilModule, providers: [DecimalPipe], imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: UtilModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ClickOutsideDirective, CurrencyFormatDirective, SlugifyPipe, TypeofPipe, IsArrayPipe],
                    imports: [CommonModule],
                    providers: [DecimalPipe],
                    exports: [ClickOutsideDirective, CurrencyFormatDirective, SlugifyPipe, TypeofPipe, IsArrayPipe]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ARROW_DOWN, ARROW_LEFT, ARROW_RIGHT, ARROW_UP, COMMA, ClickOutsideDirective, CurrencyFormatDirective, DzDomService, DzGridConstants, END, ENTER, HOME, IsArrayPipe, ListKeyManager, ResponsiveDisplayConstants, SEMICOLON, SPACE, SlugifyPipe, TAB, TypeofPipe, UtilModule, getItemScrollPosition, sameWidth };
